import React, { ReactNode } from 'react';
import { ComponentRendering, Image as SitecoreImage } from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import { Components } from '.generated/templates/Feature.Components.model';
import { Data } from '.generated/templates/Feature.Data.model';
import { Routes } from '.generated/templates/Project.Routes.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import PlaceholderWrapper from 'components/helpers/PlaceholderWrapper/PlaceholderWrapper';
import {
  alwaysSuppressLinkDataLayerPush,
  pageNavigationDataLayerHandler,
} from 'lib/util/gtm-datalayer';

export type SocialLinks = Sitecore.Override.ItemEx & Data.SocialLinks.Fields.SocialLinks;
export type GenericPage = Sitecore.Override.ItemEx & Routes.Fields.GenericPage;
export type NavigationLink = Sitecore.Override.ItemEx & Data.Navigation.Fields.NavigationLink;
export type pageorLink = GenericPage | NavigationLink;

type navigationFooter = Sitecore.Override.ItemEx &
  Data.Navigation.Fields.NavigationFooter & { children?: NavigationLink[] };

export type PublicisFooterProps = {
  fields: {
    root?: Components.Site.Fields.Footer & {
      footerLinks?: pageorLink[];
      children?: navigationFooter[];
      socialLinks?: SocialLinks[];
    };
  };
  rendering?: ComponentRendering;
};

const hoverNoUnderline = `hover:no-underline`;

const socialLinkList = (socialLinks: SocialLinks[]): ReactNode => {
  return (
    <ul className={classnames('flex', 'flex-wrap', 'pb-3')}>
      {socialLinks?.map((socilaLinkItem) => {
        return (
          socilaLinkItem && (
            <li key={socilaLinkItem.id} className={classnames('px-1.5', 'md:first:pl-0')}>
              <LinkA11yWrapper
                className={classnames('block', 'p-2.5', 'bg-gray-whisper', 'rounded-full')}
                editable={false}
                field={socilaLinkItem.fields.navigationItemLink}
                id={`social-links--${socilaLinkItem.id}`}
                rel="noreferrer noopener"
                onGtmPush={alwaysSuppressLinkDataLayerPush}
                suppressLinkText
                suppressNewTabIcon
                data-gtm-interaction-type="click: social"
                data-gtm-title={socilaLinkItem.displayName}
              >
                <SitecoreImage
                  field={socilaLinkItem.fields.icon}
                  editable={false}
                  className={classnames('justify-center', 'w-6', 'h-6', 'max-w-none')}
                />
              </LinkA11yWrapper>
            </li>
          )
        );
      })}
    </ul>
  );
};

const PublicisFooter = ({ fields, rendering }: PublicisFooterProps): JSX.Element => {
  if (!fields || fields?.root?.fields?.disclaimer === undefined) {
    return <MissingDataSource />;
  }

  const renderSocialSection = fields?.root?.fields?.socialLinks?.length > 0;

  const footerLinkList = fields?.root?.fields?.footerLinks?.map((item) => {
    const navitem = item.fields.navigationItemLink ? (item as NavigationLink) : null;
    const pageitem = !item.fields.navigationItemLink ? (item as GenericPage) : null;
    const linkfield = navitem
      ? navitem.fields.navigationItemLink
      : {
          value: {
            href: pageitem?.url,
            text:
              pageitem?.fields.navigationTitle.value ||
              pageitem?.fields.pageTitle.value ||
              pageitem?.name,
          },
        };
    return (
      <React.Fragment key={item.id}>
        <LinkA11yWrapper
          className={classnames(
            'mb-4',
            'md:mb-6',
            'md:my-2.5',
            'pr-8',
            'underline',
            hoverNoUnderline,
            'whitespace-nowrap',
            'block',
            'md:inline-block'
          )}
          editable={false}
          field={linkfield}
          suppressNewTabIcon
          onGtmPush={(e) => {
            pageNavigationDataLayerHandler(e);
          }}
        />
      </React.Fragment>
    );
  });

  const footerMainLinkList = fields?.root?.children?.map((item, index, array) => {
    return (
      <React.Fragment key={item.id}>
        <div
          className={classnames(
            'border-gray-whisper',
            'border-t',
            'last:border-b',
            'md:border-0',
            'md:last:border-0',
            'text-left',
            'md:pl-12',
            'md:first:pl-0',
            'md:last:pl-0',
            index === 0 ? null : 'last:mx-auto'
          )}
        >
          <div className={classnames('hidden', 'md:block')}>
            <h2 className={classnames('md:text-[18px]', 'font-bold', 'pb-2.5')}>
              {item?.fields?.navigationFooterHeading?.value}
            </h2>
            <ul className={classnames('gap-24', 'columns-2')}>
              {item.children?.map((link) => (
                <li key={link.id}>
                  <LinkA11yWrapper
                    className={classnames(
                      'flex',
                      'text-base',
                      'font-medium',
                      'pb-2.5',
                      'leading-4',
                      'underline',
                      hoverNoUnderline,
                      'hover:font-medium'
                    )}
                    editable={false}
                    field={link.fields.navigationItemLink}
                    suppressNewTabIcon
                    onGtmPush={(e) => {
                      pageNavigationDataLayerHandler(e);
                    }}
                  />
                </li>
              ))}
            </ul>
            {index === array.length - 1 && renderSocialSection
              ? socialLinkList(fields?.root?.fields?.socialLinks as SocialLinks[])
              : null}
          </div>
          <div className={classnames('block', 'md:hidden')}>
            <div
              className={classnames(
                'flex',
                'justify-between',
                'font-semibold',
                'py-6',
                'text-left',
                'w-full'
              )}
            >
              <h2 className={classnames('text-xl', 'font-bold', 'leading-4')}>
                {item?.fields?.navigationFooterHeading?.value}
              </h2>
            </div>
            <div className={classnames('md:pt-0', 'md:pl-0')}>
              {item.children?.map((linkItem) => (
                <LinkA11yWrapper
                  key={linkItem.id}
                  className={classnames(
                    'flex',
                    'text-base',
                    'font-medium',
                    'pb-4',
                    'leading-4',
                    'underline',
                    hoverNoUnderline,
                    'hover:font-medium'
                  )}
                  editable={false}
                  field={linkItem.fields.navigationItemLink}
                  suppressNewTabIcon
                />
              ))}
            </div>
            {index === array.length - 1 && renderSocialSection
              ? socialLinkList(fields?.root?.fields?.socialLinks as SocialLinks[])
              : null}
          </div>
        </div>
      </React.Fragment>
    );
  });

  return (
    <>
      <div data-component="authorable/site/footer">
        <div className={classnames('max-w-screen-2xl', 'mx-auto', 'pb-16')}>
          <div className={classnames('relative', 'pt-2', 'md:pt-10', 'text-center')}>
            <div
              className={classnames(
                'flex-row',
                'md:flex',
                'justify-items-auto',
                'pb-0',
                'md:pb-2',
                'px-4',
                'md:px-20'
              )}
            >
              {footerMainLinkList}
            </div>
            <div className={classnames('mx-auto')}>
              <div
                className={classnames('mx-4', 'md:mx-20', 'md:border-b', 'border-gray-whisper')}
              />
              <div
                className={classnames('px-4', 'pt-4', 'pb-0', 'text-left', 'text-sm', 'md:px-20')}
              >
                {footerLinkList}
              </div>
              <div className={classnames('px-4', 'pb-4', 'text-left', 'text-sm', 'md:px-20')}>
                <p className={classnames('mb-1')}>{fields?.root?.fields?.disclaimer.value}</p>
                <p>
                  {fields?.root?.fields?.copyright.value.replaceAll(
                    '@year',
                    new Date().getFullYear().toString()
                  )}
                </p>
              </div>
              {rendering && (
                <div className={classnames('px-4', 'pt-2', 'pb-0', 'text-left', 'md:px-20')}>
                  <PlaceholderWrapper name="nmdp-footer-content" rendering={rendering} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicisFooter;
